import { getUserInfo } from '_utils/localData'
import React from 'react'
import { withRouter } from 'react-router-dom'

import useBodyClass from '_hooks/useBodyClass'
import useQuery from '_hooks/useQuery'
import useOrderDiscount from '_hooks/useOrderDiscount'
import AddAddressDeliveryModal from '../components/AddAddressDeliveryModal'
import ChangeDeliveryAddress from '../components/ChangeDeliveryAddress'
import EventSummaryCard from './components/EventSummaryCard'
import OrderSummaryFooter from './components/OrderSummaryFooter'
import ProductListSection from './components/ProductListSection'
import PurchaseSuccessModal from './components/PurchaseSuccessModal'
import DiscountInfor from '../ManageOrder/components/DiscountInfor'
import useAddressBook from '../hooks/useAddressBook'
import useEventDetailData from '../hooks/useEventDetailData'
import useOrderSubmission from '../hooks/useOrderSubmission'
import useOrderSummary from '../hooks/useOrderSummary'
import useProductOrder from '../hooks/useProductOrder'
import { formatPrice } from '../utils/productTransforms'
import './style.scss'

const EventDetailV2 = ({ history }) => {
  useBodyClass('event-detail-v2')
  const params = useQuery()
  const userInfo = getUserInfo()

  const eid = params.get('eid') || params.get('bid')
  const oid = params.get('oid')
  const shareCode = params.get('shareCode')
  const isEventShop = params.get('eventShop')

  const eventData = useEventDetailData({ eid, oid, shareCode })
  const {
    isLoading,
    noEventFound,
    eventDetail,
    groupDetail,
    edittingOrder,
    payLater,
    adminOrHost,
    allowPlaceOrder,
    collectionTimeStart,
    collectionTimeEnd,
    checkShareEventHaveGroupIsPrivate,
    editOrderBookingAddress,
    editOrderDeliveryDate,
    productInit,
    initialComment,
    listImages,
  } = eventData

  const addressBook = useAddressBook({
    userId: userInfo?.id,
    edittingOrder,
    editOrderBookingAddress,
    editOrderDeliveryDate,
  })

  const productOrder = useProductOrder(productInit)
  const { calculatePricePerProduct, totalPrice, totalPriceEdit } = useOrderSummary({
    newOrderProducts: productOrder.newOrderProducts,
    newOrderProductsIndividual: productOrder.newOrderProductsIndividual,
    quantities: productOrder.quantities,
    productOrderRender: productOrder.productOrderRender,
    productOrderRenderIndividual: productOrder.productOrderRenderIndividual,
    findProductById: productOrder.findProductById,
  })

  const itemsTotal = edittingOrder ? totalPriceEdit : totalPrice

  const { discountAmount, originalTotal, finalTotal } = useOrderDiscount({
    orderDiscountEnabled: eventDetail?.orderDiscountEnabled,
    orderDiscount: eventDetail?.orderDiscount,
    itemsTotal,
    adminCost: eventDetail?.adminCost,
    newOrderProducts: productOrder?.newOrderProducts || [],
    quantities: productOrder?.quantities || {},
    newOrderProductsIndividual: productOrder?.newOrderProductsIndividual || [],
    edittingOrder,
  })

  const submission = useOrderSubmission({
    eid,
    oid,
    shareCode,
    isEventShop,
    edittingOrder,
    eventDetail,
    editOrderBookingAddress,
    payLater,
    initialComment,
    initTotalOrderCount: productOrder.initTotalOrderCount,
    newOrderProducts: productOrder.newOrderProducts,
    newOrderProductsIndividual: productOrder.newOrderProductsIndividual,
    quantities: productOrder.quantities,
    addressBook,
    history,
  })

  if (checkShareEventHaveGroupIsPrivate) {
    return <p id="edv2-alert-private-group">Viewing permitted for members only!!</p>
  }

  if (isLoading) {
    return <div className="edv2-page">Loading...</div>
  }

  if (noEventFound) {
    return <div className="edv2-page">No Event Found</div>
  }

  return (
    <div className="edv2-page">
      <PurchaseSuccessModal
        show={submission.showModal}
        orderNo={submission.orderNo}
        hasItemWithAckRequired={submission.hasItemWithAckRequired}
      />

      <EventSummaryCard
        eventDetail={eventDetail}
        groupDetail={groupDetail}
        listImages={listImages}
        collectionTimeStart={collectionTimeStart}
        collectionTimeEnd={collectionTimeEnd}
      />

      <DiscountInfor
        isDiscountEnabled={eventDetail?.orderDiscountEnabled}
        orderDiscount={eventDetail?.orderDiscount}
        showRequestButton={false}
      />

      <ProductListSection
        defaultItemView={eventDetail?.defaultItemView}
        edittingOrder={edittingOrder}
        products={eventDetail.products}
        productOrderRender={productOrder.productOrderRender}
        productOrderRenderIndividual={productOrder.productOrderRenderIndividual}
        newOrderProductsIndividual={productOrder.newOrderProductsIndividual}
        quantities={productOrder.quantities}
        setQuantities={productOrder.setQuantities}
        findProductById={productOrder.findProductById}
        calculatePricePerProduct={calculatePricePerProduct}
        checkMoreThanLimitPerOrder={productOrder.checkMoreThanLimitPerOrder}
        getValueSelect={productOrder.getValueSelect}
        handleChangeSubItem={productOrder.handleChangeSubItem}
        changeProductQuantity={productOrder.changeProductQuantity}
        changeProductQuantityIndividual={productOrder.changeProductQuantityIndividual}
        changeProductQuantityInput={productOrder.changeProductQuantityInput}
        addAnotherOrder={productOrder.addAnotherOrder}
        deleteItemOrder={productOrder.deleteItemOrder}
      />

      <OrderSummaryFooter
        payLater={payLater}
        totalPrice={formatPrice(finalTotal)}
        discountAmount={discountAmount}
        originalTotal={formatPrice(originalTotal)}
        adminCost={formatPrice(eventDetail.adminCost)}
        termCond={eventDetail?.termCond}
        showPurchaseOnBehalf={!oid && !shareCode && adminOrHost}
        isPurchase={submission.isPurchase}
        onTogglePurchase={() => submission.setIsPurchase((prev) => !prev)}
        phoneOrEmail={submission.phoneOrEmail}
        onChangePhoneOrEmail={submission.setPhoneOrEmail}
        bookingAddressRequired={eventDetail.bookingAddressRequired}
        edittingOrder={edittingOrder}
        displayAddress={addressBook.displayAddress}
        displayDeliveryDate={addressBook.displayDeliveryDate}
        onChangeAddressClick={addressBook.toggleChangeAddressModal}
        comment={submission.comment}
        onChangeComment={submission.setComment}
        error={submission.error}
        allowPlaceOrder={allowPlaceOrder}
        isSubmitted={submission.isSubmitted}
        onCancel={() => history.goBack()}
        onSubmit={submission.onSubmit}
      />

      {userInfo?.phone && (
        <AddAddressDeliveryModal
          isOpen={addressBook.isAddressModalOpen}
          toggle={addressBook.toggleAddressModal}
          onAddressAdded={submission.handleAddressAdded}
          phoneNumber={userInfo?.phone || ''}
          userAddresses={addressBook.userAddresses}
          eventDetail={eventDetail}
        />
      )}

      <ChangeDeliveryAddress
        isOpen={addressBook.isChangeAddressModalOpen}
        toggle={addressBook.toggleChangeAddressModal}
        userAddresses={addressBook.userAddresses}
        selectedAddress={addressBook.selectedAddressForModal}
        onAddressSelected={submission.handleAddressSelectedAndProceed}
        onAddNewAddress={addressBook.handleOpenAddNewAddress}
        onAddressUpdated={addressBook.refreshAddresses}
        onAddressAdded={submission.handleAddressAddedAndProceed}
        eventDetail={eventDetail}
      />
    </div>
  )
}

export default withRouter(EventDetailV2)
